/**
 * Inline citation pill rendered in place of every [Ref N] token in
 * the narrative. Click scrolls the matching ReferencesSection list
 * item into view and flashes a brief highlight ring; hover shows the
 * source name (and URL when present) via the native title tooltip.
 *
 * Pairs with `walkCitationText` (src/lib/walkCitationText.tsx), which
 * splits raw narrative text on /\[Ref \d+\]/ and replaces each token
 * with a <RefLink/>. Refs beyond `references.length` (or filtered out of
 * the appendix) render NOTHING — an unresolvable citation marker is
 * REMOVED from the customer-facing surface and logged (console.warn) so
 * the degradation is visible to ops without shipping a literal `[?]`
 * placeholder into the report (Redhead review wave 3, defect 1).
 *
 * REF-LINEAGE REF-3 — when the host (`walkCitationText`) supplies
 * `linkedDocumentRef` (with `pageNumber`) AND `linkedDocument`, the
 * click handler ALSO opens `DocViewerDialog` at the recorded page.
 * The scroll-to-appendix step still fires — the deep-link is ADDITIVE.
 * Both new props are optional so the legacy `<RefLink n reference />`
 * call shape (every site that doesn't have an SED handy: test fixtures,
 * narrow snippet renderers, PDF export pipeline) keeps working
 * unchanged.
 */
import React, { useContext, useState } from 'react';
import type { DataSource, SiteDocument } from '../../../types/siteRag';
import type { LinkedDocumentRef } from '../../../types/siteEvidence';
import { markerIdToSuperscript } from '../../../lib/citations/amendmentMarker';
import { AmendmentMarkerSpans } from './AmendmentMarkerSpans';
import { DocViewerDialog } from '../DocViewerDialog';
import { resolveLinkedDocDeepLink } from '../../../lib/finDecision/resolveCitationDeepLink';
import {
  isRetiredSimulationDataSource,
  LEGACY_SIMULATION_REFERENCE_LABEL,
  LEGACY_SIMULATION_REGEN_GUIDANCE,
} from '../../../lib/reportGeneration/legacySimulationRetirement';

export interface RefLinkProps {
  /** 1-based citation index — must match the appendix list ordering. */
  n: number;
  /** Resolved reference (`references[n-1]`) when in-bounds, undefined otherwise. */
  reference?: DataSource;
  /**
   * REF-LINEAGE REF-3 — when supplied with a `pageNumber`, the click
   * handler ALSO opens `DocViewerDialog` at the recorded page. The
   * scroll + highlight still fires; the dialog is additive. Resolved
   * by `walkCitationText` via `resolveDocumentLineage(ref, sed)`.
   * Omit (legacy callers) and click is scroll-only.
   */
  linkedDocumentRef?: LinkedDocumentRef;
  /**
   * REF-LINEAGE REF-3 — the `SiteDocument` to load into the viewer.
   * Resolved by `walkCitationText` from `docs[]` against
   * `linkedDocumentRef.docId`. Required to actually open the viewer
   * (the viewer needs the doc object); omit and click is scroll-only
   * (the tooltip still surfaces the page/heading lineage so reviewers
   * see the linkage even without the deep-link).
   */
  linkedDocument?: SiteDocument;
  /** When true (OOB or missing row), the marker renders NOTHING (removed +
   *  logged) — never a literal `[?]` placeholder. */
  outOfBounds?: boolean;
}

const HIGHLIGHT_DURATION_MS = 1500;
const HIGHLIGHT_CLASSES = ['ring-2', 'ring-primary'];

/**
 * PG-9a (Golden-B) — render surface for the inline `[Ref N]` pill.
 *
 * On the INTERACTIVE surface (default) the pill is a `<button>` whose
 * `onClick` smooth-scrolls the appendix row into view + flashes the
 * highlight ring (and, for doc-backed refs, opens the viewer). There is
 * no JS event loop in a saved PDF, so on the PRINT surface the pill is
 * instead an `<a href="#ref-N">` — a real in-document hash link that
 * Paged.js / Puppeteer turn into a clickable internal bookmark, so the
 * inline-citation → appendix jump survives into the exported PDF. The
 * `onClick` handler is preserved on the anchor too (additive: harmless
 * in the static PDF, and the interactive /print preview still scrolls),
 * with `preventDefault` so an interactive realm keeps the smooth-scroll
 * rather than a jarring native hash jump.
 *
 * Defaults to `'view'` so the on-screen viewer + every existing caller
 * (and the RefLink unit tests) keep the exact `<button>` DOM they have
 * today — only `PrintReportView`'s print subtree provides `'print'`.
 */
export type RefLinkSurface = 'view' | 'print';
export const RefLinkSurfaceContext = React.createContext<RefLinkSurface>('view');

function amendmentMarkerIdsForReference(reference?: DataSource): string[] {
  const maybe = (reference as DataSource & { amendmentMarkerIds?: unknown } | undefined)?.amendmentMarkerIds;
  return Array.isArray(maybe) ? maybe.filter((id): id is string => typeof id === 'string') : [];
}

export function RefLink({
  n,
  reference,
  linkedDocumentRef,
  linkedDocument,
  outOfBounds = false,
}: RefLinkProps): React.ReactElement {
  // REF-LINEAGE REF-3 — viewer open state. Local to each pill so
  // multiple `[Ref N]` clicks on the same page don't collide (Radix's
  // Dialog supports multiple instances). Defaults closed; click flips
  // it true; DocViewerDialog's onClose flips it back.
  const [viewerOpen, setViewerOpen] = useState(false);

  // PG-9a — print surface renders an <a href="#ref-N"> instead of a
  // <button> so the inline citation is a navigable PDF bookmark. View
  // surface (default) keeps the button + JS scroll behaviour unchanged.
  const surface = useContext(RefLinkSurfaceContext);

  // H-4 (passage-offset citation deep-links) — resolve the most specific
  // deep-link target the ref carries. `resolveLinkedDocDeepLink` prefers
  // the verbatim-passage `sourceOffset` (offset precision), then
  // `pageNumber` (page precision, today's REF-LINEAGE behaviour), then
  // filename. Returns `precision: 'none'` for a ref with no target — the
  // pill then stays scroll-only (legacy behaviour). The deep-link is
  // ADDITIVE: the appendix scroll always fires regardless of precision.
  const deepLink = resolveLinkedDocDeepLink(linkedDocumentRef);
  const isOffset = deepLink.precision === 'offset';
  // True when this pill carries a deep-linkable doc lineage (offset OR
  // page) — exposed via `data-has-lineage` for the REF-AUDIT smoke check
  // (item 3) and for any future selector-based tests / styling.
  const hasLineage = isOffset || deepLink.precision === 'page';
  const canOpenViewer = hasLineage && Boolean(linkedDocument);

  const handleClick = (event?: React.MouseEvent): void => {
    // PG-9a — on the print surface the pill is an <a href="#ref-N">; in
    // an INTERACTIVE realm (the /print preview) we still want the smooth
    // scroll, not the browser's native jump-to-anchor, so swallow the
    // default navigation. In a static PDF there is no event loop, so the
    // anchor's href is what carries the (now clickable) bookmark jump.
    if (event && surface === 'print') event.preventDefault();
    // 1. (UNCHANGED) Scroll the appendix row into view — today contract.
    //    The [Ref N] indexing + appendix anchor are sacrosanct; this
    //    step fires for every ref including doc-backed ones.
    if (typeof document !== 'undefined') {
      const el = document.getElementById(`ref-${n}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add(...HIGHLIGHT_CLASSES);
        // setTimeout is fine here — even if the user navigates away
        // the DOM node holding the class will be unmounted; no leak.
        setTimeout(() => el.classList.remove(...HIGHLIGHT_CLASSES), HIGHLIGHT_DURATION_MS);
      }
    }
    // 2. (NEW) Doc-backed ref + doc supplied → open the viewer too.
    //    Additive: never replaces the scroll behaviour above. When
    //    either field is missing (legacy SED, no doc resolved) the
    //    pill stays scroll-only and the user falls back to the
    //    today behaviour.
    if (canOpenViewer) setViewerOpen(true);
  };

  // Tooltip — when lineage is present, prefer a framing that tells the
  // user what the click will reveal. Offset precision deep-links to the
  // exact passage; page precision opens the page. Otherwise today's
  // "source · url" / "(not found in appendix)" branches.
  const heading = linkedDocumentRef?.sectionHeading ? ` · ${linkedDocumentRef.sectionHeading}` : '';
  const lineageTooltip = isOffset
    ? `Open source – exact passage${heading}`
    : hasLineage
      ? `Open source – page ${linkedDocumentRef!.pageNumber}${heading}`
      : null;
  const isOob = outOfBounds || n < 1 || (!reference && n > 0);
  // Redhead review wave 3 (defect 1) — an UNRESOLVABLE citation marker is
  // REMOVED from the rendered surface (never a literal `[?]` placeholder in
  // customer-facing prose) and the removal is logged so reviewers/ops still
  // see that a hallucinated / filtered token survived to the render layer.
  if (isOob) {
    console.warn(
      `[RefLink] removed unresolvable inline citation marker [Ref ${n}] — `
      + 'no matching appendix row (out-of-bounds or filtered)',
    );
    return <></>;
  }
  const amendmentMarkerIds = amendmentMarkerIdsForReference(reference);
  if (isRetiredSimulationDataSource(reference)) {
    return (
      <>
        <span
          className="inline-flex align-super whitespace-nowrap text-[0.7em] text-muted-foreground ml-0.5"
          title={LEGACY_SIMULATION_REGEN_GUIDANCE}
          data-ref={n}
          data-retired-simulation-reference="true"
        >
          {LEGACY_SIMULATION_REFERENCE_LABEL}
        </span>
        {amendmentMarkerIds.length > 0 ? (
          <AmendmentMarkerSpans
            markerIds={amendmentMarkerIds}
            idToChar={markerIdToSuperscript}
          />
        ) : null}
      </>
    );
  }
  // William QA 2026-06-29 — inline citations render as a SUPERSCRIPT `[n]`
  // (drop the "Ref" word; the appendix hyperlink + #ref-n anchor are unchanged).
  const displayLabel = `[${n}]`;

  const title = lineageTooltip
    ?? (reference
      ? `${reference.source}${reference.url ? ' · ' + reference.url : ''}`
      : `Reference ${n} (not found in appendix)`);

  // PG-9a — props shared by both surfaces. `whitespace-nowrap` so the
  // marker never wraps internally, and a tiny LEFT margin (no symmetric
  // `px-0.5`, which added a visible gap on both sides + made the
  // inline-flex pill its own line-break opportunity) so it hugs the
  // preceding word and reads as a superscript-style citation rather than
  // a floating chip in the narrow two-column print flow. RRQ CITATION FIX.
  const sharedProps = {
    onClick: handleClick,
    title,
    className:
      'inline-flex align-super whitespace-nowrap text-[0.7em] text-primary hover:underline ml-0.5 cursor-pointer',
    'data-ref': n,
    'data-has-lineage': hasLineage ? 'true' : 'false',
    // H-4 — expose the resolved deep-link precision (offset | page |
    // filename | none) so the REF-AUDIT smoke check + tests can assert
    // the passage-vs-page tier without reaching into the ref shape.
    'data-link-precision': deepLink.precision,
  } as const;

  return (
    <>
      {surface === 'print' ? (
        // PG-9a — print surface: a real in-document hash link. PDF engines
        // (Paged.js / Puppeteer) convert `#ref-N` into a clickable internal
        // bookmark, so the inline citation → appendix jump survives export.
        <a href={`#ref-${n}`} {...sharedProps}>
          {displayLabel}
        </a>
      ) : (
        <button type="button" {...sharedProps}>
          {displayLabel}
        </button>
      )}
      {amendmentMarkerIds.length > 0 ? (
        <AmendmentMarkerSpans
          markerIds={amendmentMarkerIds}
          idToChar={markerIdToSuperscript}
        />
      ) : null}
      {/* REF-LINEAGE REF-3 — viewer mount. Guarded by
           `canOpenViewer` so we never render an empty Dialog on a
           ref that can't open one. `doc={viewerOpen ? linkedDocument
           : null}` mirrors the DocViewerDialog contract (a null doc
           short-circuits the render). */}
      {canOpenViewer && (
        <DocViewerDialog
          doc={viewerOpen ? linkedDocument! : null}
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          // H-4 — offset precision deep-links to the exact passage (opens
          // the extracted-text view with the span highlighted); page
          // precision opens the page (today's REF-LINEAGE behaviour). The
          // page is still passed alongside an offset so a viewer that
          // ignores the offset (e.g. a paginated PDF preview) can still
          // land on the right page.
          initialPage={deepLink.page ?? linkedDocumentRef!.pageNumber}
          highlightOffset={isOffset ? deepLink.offset : undefined}
        />
      )}
    </>
  );
}
